import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion, AnimatePresence, Reorder } from 'framer-motion';
import { 
  Plus, Search, Filter, ArrowLeft, Phone, Mail, Building, MapPin,
  Instagram, Linkedin, Calendar, Link as LinkIcon, Handshake, Megaphone,
  MoreVertical, Edit, Trash2, CheckSquare, MessageSquare, Clock,
  X, Save, User, Briefcase, Users as UsersIcon, Globe, Hash
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { toast } from 'sonner';

import { API_URL } from '../../../config/api';

const pipelineConfig = {
  instagram: { name: 'Prospection Instagram', icon: Instagram, color: 'from-pink-500 to-purple-500', headerBg: 'bg-gradient-to-r from-pink-500 to-purple-500' },
  linkedin: { name: 'LinkedIn Growth', icon: Linkedin, color: 'from-blue-600 to-blue-800', headerBg: 'bg-gradient-to-r from-blue-600 to-blue-800' },
  partenaires: { name: 'Partenaires stratégiques', icon: Handshake, color: 'from-emerald-500 to-teal-600', headerBg: 'bg-gradient-to-r from-emerald-500 to-teal-600' },
  influenceurs: { name: 'Influenceurs BTP', icon: Megaphone, color: 'from-orange-500 to-red-500', headerBg: 'bg-gradient-to-r from-orange-500 to-red-500' },
  salons: { name: 'Salons et événements', icon: Calendar, color: 'from-violet-500 to-purple-600', headerBg: 'bg-gradient-to-r from-violet-500 to-purple-600' },
  affiliation: { name: 'Programme affiliation', icon: LinkIcon, color: 'from-cyan-500 to-blue-500', headerBg: 'bg-gradient-to-r from-cyan-500 to-blue-500' },
};

const metierOptions = [
  'Maçonnerie', 'Plomberie', 'Électricité', 'Carrelage', 'Peinture',
  'Menuiserie', 'Charpente/Couverture', 'Plâtrerie', 'Chauffage/Climatisation',
  'Terrassement', 'Isolation', 'Pisciniste', 'Jardinier', 'Paysagiste',
  'Serrurerie', 'Façade', 'Multi-corps d\'état', 'Autre'
];

const partnerTypes = ['Artisan', 'Fournisseur', 'Influenceur', 'Formateur', 'Autre'];
const platforms = ['Instagram', 'TikTok', 'YouTube', 'Facebook', 'LinkedIn'];

export default function PipelineKanban() {
  const { pipelineType } = useParams();
  const [stages, setStages] = useState([]);
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedCard, setSelectedCard] = useState(null);
  const [selectedStage, setSelectedStage] = useState(null);
  const [draggedCard, setDraggedCard] = useState(null);

  const config = pipelineConfig[pipelineType] || {};
  const Icon = config.icon || Calendar;

  useEffect(() => {
    if (pipelineType) {
      fetchPipelineData();
    }
  }, [pipelineType]);

  const fetchPipelineData = async () => {
    const token = localStorage.getItem('admin_token');
    if (!token) return;

    setLoading(true);
    try {
      const [stagesRes, cardsRes] = await Promise.all([
        fetch(`${API_URL}/api/commercial/pipelines/${pipelineType}/stages`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/commercial/cards?pipeline_type=${pipelineType}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      if (stagesRes.ok) setStages(await stagesRes.json());
      if (cardsRes.ok) setCards(await cardsRes.json());
    } catch (error) {
      console.error('Error:', error);
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCard = async (formData) => {
    const token = localStorage.getItem('admin_token');
    if (!token) return;

    try {
      const response = await fetch(`${API_URL}/api/commercial/cards`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          pipeline_type: pipelineType,
          stage_id: selectedStage || stages[0]?.id,
          ...formData
        })
      });

      if (response.ok) {
        toast.success('Carte créée !');
        setShowCreateModal(false);
        fetchPipelineData();
      } else {
        toast.error('Erreur lors de la création');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    }
  };

  const handleMoveCard = async (cardId, newStageId, newPosition) => {
    const token = localStorage.getItem('admin_token');
    if (!token) return;

    // Optimistic update
    setCards(prev => prev.map(c => 
      c.id === cardId ? { ...c, stage_id: newStageId, position: newPosition } : c
    ));

    try {
      await fetch(`${API_URL}/api/commercial/cards/${cardId}/move`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ stage_id: newStageId, position: newPosition })
      });
    } catch (error) {
      console.error('Error:', error);
      fetchPipelineData(); // Revert on error
    }
  };

  const handleDeleteCard = async (cardId) => {
    if (!confirm('Supprimer cette carte ?')) return;
    
    const token = localStorage.getItem('admin_token');
    try {
      const response = await fetch(`${API_URL}/api/commercial/cards/${cardId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        toast.success('Carte supprimée');
        fetchPipelineData();
        setShowDetailModal(false);
      }
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const getCardsForStage = (stageId) => {
    return cards
      .filter(c => c.stage_id === stageId)
      .filter(c => {
        if (!searchQuery) return true;
        const query = searchQuery.toLowerCase();
        return (
          c.name?.toLowerCase().includes(query) ||
          c.company_name?.toLowerCase().includes(query) ||
          c.email?.toLowerCase().includes(query)
        );
      })
      .sort((a, b) => a.position - b.position);
  };

  const handleDragStart = (e, card) => {
    setDraggedCard(card);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, stageId) => {
    e.preventDefault();
    if (draggedCard && draggedCard.stage_id !== stageId) {
      const stageCards = getCardsForStage(stageId);
      handleMoveCard(draggedCard.id, stageId, stageCards.length);
    }
    setDraggedCard(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col" data-testid={`pipeline-${pipelineType}`}>
      {/* Header - Responsive */}
      <div className={`${config.headerBg} text-white px-3 sm:px-6 py-3 sm:py-4 rounded-t-xl`}>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-2 sm:gap-4">
            <Link to="/admin/commercial" className="p-2 hover:bg-white/20 rounded-lg transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div className="flex items-center gap-2 sm:gap-3">
              <Icon className="w-6 h-6 sm:w-8 sm:h-8 flex-shrink-0" />
              <div className="min-w-0">
                <h1 className="text-lg sm:text-2xl font-bold truncate">{config.name}</h1>
                <p className="text-white/80 text-xs sm:text-sm">{cards.length} prospects</p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="relative flex-1 sm:flex-none">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/60" />
              <input
                type="text"
                placeholder="Rechercher..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full sm:w-40 lg:w-48 pl-9 pr-3 py-2 bg-white/20 border border-white/30 rounded-lg text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-white/50 text-sm"
              />
            </div>
            <Button 
              onClick={() => { setSelectedStage(stages[0]?.id); setShowCreateModal(true); }}
              className="bg-white text-gray-900 hover:bg-white/90 min-h-[44px] px-3 sm:px-4"
            >
              <Plus className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Ajouter</span>
            </Button>
          </div>
        </div>
      </div>

      {/* Kanban Board - Responsive */}
      <div className="flex-1 overflow-x-auto bg-gray-100 p-2 sm:p-4">
        <div className="flex gap-2 sm:gap-4 h-full min-w-max pb-4">
          {stages.map((stage, stageIndex) => {
            const stageCards = getCardsForStage(stage.id);
            return (
              <div
                key={stage.id}
                className="w-[280px] sm:w-80 flex-shrink-0 bg-gray-50 rounded-xl flex flex-col"
                onDragOver={handleDragOver}
                onDrop={(e) => handleDrop(e, stage.id)}
                data-testid={`stage-${stage.id}`}
              >
                {/* Stage Header */}
                <div className="p-3 sm:p-4 border-b border-gray-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div 
                        className="w-3 h-3 rounded-full flex-shrink-0"
                        style={{ backgroundColor: stage.color }}
                      />
                      <h3 className="font-semibold text-gray-900 text-sm sm:text-base">{stage.name}</h3>
                    </div>
                    <span className="text-xs sm:text-sm font-medium text-gray-500 bg-gray-200 px-2 py-0.5 rounded-full">
                      {stageCards.length}
                    </span>
                  </div>
                </div>

                {/* Cards Container */}
                <div className="flex-1 overflow-y-auto p-2 sm:p-3 space-y-2 sm:space-y-3">
                  <AnimatePresence>
                    {stageCards.map((card) => (
                      <motion.div
                        key={card.id}
                        layout
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.9 }}
                        draggable
                        onDragStart={(e) => handleDragStart(e, card)}
                        onClick={() => { setSelectedCard(card); setShowDetailModal(true); }}
                        className={`bg-white rounded-lg p-3 sm:p-4 shadow-sm border border-gray-200 cursor-pointer hover:shadow-md hover:border-gray-300 transition-all active:scale-[0.98] ${
                          draggedCard?.id === card.id ? 'opacity-50' : ''
                        }`}
                        data-testid={`card-${card.id}`}
                      >
                        <div className="flex items-start justify-between gap-2 mb-2">
                          <h4 className="font-semibold text-gray-900 text-sm sm:text-base line-clamp-2">{card.name}</h4>
                          <span 
                            className={`text-[10px] sm:text-xs px-1.5 sm:px-2 py-0.5 rounded-full whitespace-nowrap flex-shrink-0 ${
                              card.status === 'client' ? 'bg-green-100 text-green-700' :
                              card.status === 'proposition' ? 'bg-orange-100 text-orange-700' :
                              card.status === 'en_discussion' ? 'bg-blue-100 text-blue-700' :
                              'bg-gray-100 text-gray-700'
                            }`}
                          >
                            {card.status === 'client' ? 'Client' :
                             card.status === 'proposition' ? 'Proposition' :
                             card.status === 'en_discussion' ? 'Discussion' : 'Prospect'}
                          </span>
                        </div>
                        
                        {card.company_name && (
                          <p className="text-xs sm:text-sm text-gray-600 flex items-center gap-1 mb-1">
                            <Building className="w-3 h-3 flex-shrink-0" />
                            <span className="truncate">{card.company_name}</span>
                          </p>
                        )}
                        
                        {card.city && (
                          <p className="text-xs sm:text-sm text-gray-500 flex items-center gap-1 mb-1">
                            <MapPin className="w-3 h-3 flex-shrink-0" />
                            <span className="truncate">{card.city}</span>
                          </p>
                        )}

                        {card.metier && (
                          <p className="text-xs sm:text-sm text-gray-500 flex items-center gap-1">
                            <Briefcase className="w-3 h-3 flex-shrink-0" />
                            <span className="truncate">{card.metier}</span>
                          </p>
                        )}

                        {/* Pipeline-specific info */}
                        {pipelineType === 'instagram' && card.instagram_handle && (
                          <p className="text-xs sm:text-sm text-pink-600 flex items-center gap-1 mt-2">
                            <Instagram className="w-3 h-3 flex-shrink-0" />
                            <span className="truncate">@{card.instagram_handle}</span>
                          </p>
                        )}

                        {pipelineType === 'linkedin' && card.linkedin_url && (
                          <p className="text-xs sm:text-sm text-blue-600 flex items-center gap-1 mt-2">
                            <Linkedin className="w-3 h-3 flex-shrink-0" />
                            Profil LinkedIn
                          </p>
                        )}

                        {pipelineType === 'influenceurs' && card.followers_count && (
                          <p className="text-xs sm:text-sm text-orange-600 flex items-center gap-1 mt-2">
                            <UsersIcon className="w-3 h-3 flex-shrink-0" />
                            {card.followers_count.toLocaleString()} abonnes
                          </p>
                        )}

                        {/* Tasks indicator */}
                        {card.tasks?.length > 0 && (
                          <div className="flex items-center gap-2 mt-2 sm:mt-3 pt-2 sm:pt-3 border-t border-gray-100">
                            <CheckSquare className="w-3 h-3 text-gray-400" />
                            <span className="text-[10px] sm:text-xs text-gray-500">
                              {card.tasks.filter(t => t.is_completed).length}/{card.tasks.length} taches
                            </span>
                          </div>
                        )}
                      </motion.div>
                    ))}
                  </AnimatePresence>

                  {/* Add card button for each stage */}
                  <button
                    onClick={() => { setSelectedStage(stage.id); setShowCreateModal(true); }}
                    className="w-full p-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-500 hover:border-gray-400 hover:text-gray-600 active:bg-gray-100 transition-colors flex items-center justify-center gap-2 min-h-[48px]"
                  >
                    <Plus className="w-4 h-4" />
                    <span className="text-xs sm:text-sm">Ajouter</span>
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Create Modal */}
      <CreateCardModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSubmit={handleCreateCard}
        pipelineType={pipelineType}
        stageName={stages.find(s => s.id === selectedStage)?.name}
      />

      {/* Detail Modal */}
      <CardDetailModal
        isOpen={showDetailModal}
        onClose={() => setShowDetailModal(false)}
        card={selectedCard}
        pipelineType={pipelineType}
        stages={stages}
        onDelete={handleDeleteCard}
        onUpdate={fetchPipelineData}
      />
    </div>
  );
}

// ============ CREATE CARD MODAL ============

function CreateCardModal({ isOpen, onClose, onSubmit, pipelineType, stageName }) {
  const [formData, setFormData] = useState({
    name: '',
    company_name: '',
    phone: '',
    email: '',
    city: '',
    metier: '',
    source: '',
    notes: '',
    instagram_handle: '',
    linkedin_url: '',
    job_title: '',
    partner_type: '',
    audience_size: '',
    platform: '',
    followers_count: '',
    niche: '',
    event_name: '',
    affiliate_link: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.name) {
      toast.error('Le nom est requis');
      return;
    }
    // Clean data: convert empty strings to null for numeric fields
    const cleanData = {
      ...formData,
      audience_size: formData.audience_size ? parseInt(formData.audience_size) : null,
      followers_count: formData.followers_count ? parseInt(formData.followers_count) : null,
      employee_count: formData.employee_count ? parseInt(formData.employee_count) : null,
    };
    // Remove empty string values
    Object.keys(cleanData).forEach(key => {
      if (cleanData[key] === '') cleanData[key] = null;
    });
    onSubmit(cleanData);
    setFormData({
      name: '', company_name: '', phone: '', email: '', city: '', metier: '',
      source: '', notes: '', instagram_handle: '', linkedin_url: '', job_title: '',
      partner_type: '', audience_size: '', platform: '', followers_count: '', niche: '',
      event_name: '', affiliate_link: ''
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
      <motion.div
        initial={{ opacity: 0, y: 100 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white w-full sm:rounded-2xl sm:w-full sm:max-w-2xl max-h-[95vh] sm:max-h-[90vh] overflow-y-auto rounded-t-2xl"
      >
        <div className="p-4 sm:p-6 border-b border-gray-100 flex items-center justify-between sticky top-0 bg-white z-10">
          <div>
            <h2 className="text-lg sm:text-xl font-bold text-gray-900">Nouvelle carte</h2>
            {stageName && <p className="text-xs sm:text-sm text-gray-500">Etape : {stageName}</p>}
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg min-h-[44px] min-w-[44px] flex items-center justify-center">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 sm:p-6 space-y-4 sm:space-y-6">
          {/* Common fields */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Nom / Dirigeant *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-3 sm:px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                placeholder="Jean Dupont"
                required
              />
            </div>
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Entreprise</label>
              <input
                type="text"
                value={formData.company_name}
                onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                className="w-full px-3 sm:px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                placeholder="BTP Excellence"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Telephone</label>
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="w-full px-3 sm:px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                placeholder="+33 6 12 34 56 78"
              />
            </div>
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Email</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full px-3 sm:px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                placeholder="contact@entreprise.fr"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Ville</label>
              <input
                type="text"
                value={formData.city}
                onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                className="w-full px-3 sm:px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                placeholder="Lyon"
              />
            </div>
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Metier</label>
              <select
                value={formData.metier}
                onChange={(e) => setFormData({ ...formData, metier: e.target.value })}
                className="w-full px-3 sm:px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              >
                <option value="">Selectionner...</option>
                {metierOptions.map(m => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
          </div>

          {/* Instagram specific */}
          {pipelineType === 'instagram' && (
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Compte Instagram</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">@</span>
                <input
                  type="text"
                  value={formData.instagram_handle}
                  onChange={(e) => setFormData({ ...formData, instagram_handle: e.target.value })}
                  className="w-full pl-8 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent text-sm"
                  placeholder="compte_instagram"
                />
              </div>
            </div>
          )}

          {/* LinkedIn specific */}
          {pipelineType === 'linkedin' && (
            <>
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">URL Profil LinkedIn</label>
                <input
                  type="url"
                  value={formData.linkedin_url}
                  onChange={(e) => setFormData({ ...formData, linkedin_url: e.target.value })}
                  className="w-full px-3 sm:px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  placeholder="https://linkedin.com/in/..."
                />
              </div>
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Poste</label>
                <input
                  type="text"
                  value={formData.job_title}
                  onChange={(e) => setFormData({ ...formData, job_title: e.target.value })}
                  className="w-full px-3 sm:px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  placeholder="Gerant, Directeur..."
                />
              </div>
            </>
          )}

          {/* Partenaires specific */}
          {pipelineType === 'partenaires' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Type de partenaire</label>
                <select
                  value={formData.partner_type}
                  onChange={(e) => setFormData({ ...formData, partner_type: e.target.value })}
                  className="w-full px-3 sm:px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-sm"
                >
                  <option value="">Selectionner...</option>
                  {partnerTypes.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Taille audience</label>
                <input
                  type="number"
                  value={formData.audience_size}
                  onChange={(e) => setFormData({ ...formData, audience_size: e.target.value })}
                  className="w-full px-3 sm:px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-sm"
                  placeholder="10000"
                />
              </div>
            </div>
          )}

          {/* Influenceurs specific */}
          {pipelineType === 'influenceurs' && (
            <>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Plateforme</label>
                  <select
                    value={formData.platform}
                    onChange={(e) => setFormData({ ...formData, platform: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  >
                    <option value="">Sélectionner...</option>
                    {platforms.map(p => <option key={p} value={p}>{p}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nombre d'abonnés</label>
                  <input
                    type="number"
                    value={formData.followers_count}
                    onChange={(e) => setFormData({ ...formData, followers_count: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    placeholder="50000"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Niche</label>
                <input
                  type="text"
                  value={formData.niche}
                  onChange={(e) => setFormData({ ...formData, niche: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  placeholder="BTP, Rénovation, Maçonnerie..."
                />
              </div>
            </>
          )}

          {/* Salons specific */}
          {pipelineType === 'salons' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nom du salon/événement</label>
              <input
                type="text"
                value={formData.event_name}
                onChange={(e) => setFormData({ ...formData, event_name: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                placeholder="Batimat 2026"
              />
            </div>
          )}

          {/* Affiliation specific */}
          {pipelineType === 'affiliation' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Lien affilié</label>
              <input
                type="url"
                value={formData.affiliate_link}
                onChange={(e) => setFormData({ ...formData, affiliate_link: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                placeholder="https://batiassist.fr/ref/..."
              />
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Source</label>
            <input
              type="text"
              value={formData.source}
              onChange={(e) => setFormData({ ...formData, source: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Recherche directe, Recommandation..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows={3}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Notes sur ce prospect..."
            />
          </div>

          <div className="flex gap-3 pt-4 border-t">
            <Button type="button" variant="outline" onClick={onClose} className="flex-1">
              Annuler
            </Button>
            <Button type="submit" className="flex-1 bg-blue-600 hover:bg-blue-700">
              <Save className="w-4 h-4 mr-2" />
              Créer
            </Button>
          </div>
        </form>
      </motion.div>
    </div>
  );
}

// ============ CARD DETAIL MODAL ============

function CardDetailModal({ isOpen, onClose, card, pipelineType, stages, onDelete, onUpdate }) {
  const [activeTab, setActiveTab] = useState('info');
  const [newTask, setNewTask] = useState('');
  const [newNote, setNewNote] = useState('');

  if (!isOpen || !card) return null;

  const handleAddTask = async () => {
    if (!newTask.trim()) return;
    const token = localStorage.getItem('admin_token');
    try {
      await fetch(`${API_URL}/api/commercial/cards/${card.id}/tasks`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ title: newTask })
      });
      setNewTask('');
      onUpdate();
      toast.success('Tâche ajoutée');
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    const token = localStorage.getItem('admin_token');
    try {
      await fetch(`${API_URL}/api/commercial/cards/${card.id}/notes`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ content: newNote })
      });
      setNewNote('');
      onUpdate();
      toast.success('Note ajoutée');
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const currentStage = stages.find(s => s.id === card.stage_id);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col"
      >
        {/* Header */}
        <div className="p-6 border-b border-gray-100 flex items-start justify-between">
          <div>
            <h2 className="text-xl font-bold text-gray-900">{card.name}</h2>
            {card.company_name && (
              <p className="text-gray-500 flex items-center gap-1 mt-1">
                <Building className="w-4 h-4" />
                {card.company_name}
              </p>
            )}
            <div className="flex items-center gap-2 mt-2">
              <span 
                className="px-2 py-1 rounded-full text-xs font-medium"
                style={{ backgroundColor: currentStage?.color + '20', color: currentStage?.color }}
              >
                {currentStage?.name}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => onDelete(card.id)} className="text-red-600 hover:bg-red-50">
              <Trash2 className="w-4 h-4" />
            </Button>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-100">
          {['info', 'tasks', 'history'].map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-6 py-3 font-medium transition-colors ${
                activeTab === tab 
                  ? 'text-blue-600 border-b-2 border-blue-600' 
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab === 'info' ? 'Informations' : tab === 'tasks' ? 'Tâches' : 'Historique'}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {activeTab === 'info' && (
            <div className="space-y-6">
              {/* Contact Info */}
              <div className="grid md:grid-cols-2 gap-4">
                {card.phone && (
                  <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                    <Phone className="w-5 h-5 text-gray-400" />
                    <div>
                      <p className="text-xs text-gray-500">Téléphone</p>
                      <p className="font-medium">{card.phone}</p>
                    </div>
                  </div>
                )}
                {card.email && (
                  <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                    <Mail className="w-5 h-5 text-gray-400" />
                    <div>
                      <p className="text-xs text-gray-500">Email</p>
                      <p className="font-medium">{card.email}</p>
                    </div>
                  </div>
                )}
                {card.city && (
                  <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                    <MapPin className="w-5 h-5 text-gray-400" />
                    <div>
                      <p className="text-xs text-gray-500">Ville</p>
                      <p className="font-medium">{card.city}</p>
                    </div>
                  </div>
                )}
                {card.metier && (
                  <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                    <Briefcase className="w-5 h-5 text-gray-400" />
                    <div>
                      <p className="text-xs text-gray-500">Métier</p>
                      <p className="font-medium">{card.metier}</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Pipeline specific info */}
              {card.instagram_handle && (
                <div className="flex items-center gap-3 p-3 bg-pink-50 rounded-lg">
                  <Instagram className="w-5 h-5 text-pink-500" />
                  <div>
                    <p className="text-xs text-pink-600">Instagram</p>
                    <p className="font-medium text-pink-700">@{card.instagram_handle}</p>
                  </div>
                </div>
              )}

              {card.linkedin_url && (
                <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg">
                  <Linkedin className="w-5 h-5 text-blue-600" />
                  <div>
                    <p className="text-xs text-blue-600">LinkedIn</p>
                    <a href={card.linkedin_url} target="_blank" rel="noopener noreferrer" className="font-medium text-blue-700 hover:underline">
                      Voir le profil
                    </a>
                  </div>
                </div>
              )}

              {/* Notes */}
              {card.notes && (
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Notes</h4>
                  <p className="text-gray-600 bg-gray-50 p-4 rounded-lg whitespace-pre-wrap">{card.notes}</p>
                </div>
              )}

              {/* Add note */}
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Ajouter une note</h4>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newNote}
                    onChange={(e) => setNewNote(e.target.value)}
                    placeholder="Nouvelle note..."
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    onKeyPress={(e) => e.key === 'Enter' && handleAddNote()}
                  />
                  <Button onClick={handleAddNote}>Ajouter</Button>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'tasks' && (
            <div className="space-y-4">
              {/* Add task */}
              <div className="flex gap-2">
                <input
                  type="text"
                  value={newTask}
                  onChange={(e) => setNewTask(e.target.value)}
                  placeholder="Nouvelle tâche..."
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  onKeyPress={(e) => e.key === 'Enter' && handleAddTask()}
                />
                <Button onClick={handleAddTask}>
                  <Plus className="w-4 h-4 mr-2" />
                  Ajouter
                </Button>
              </div>

              {/* Tasks list */}
              <div className="space-y-2">
                {card.tasks?.length > 0 ? (
                  card.tasks.map(task => (
                    <div key={task.id} className={`flex items-center gap-3 p-3 rounded-lg ${task.is_completed ? 'bg-green-50' : 'bg-gray-50'}`}>
                      <CheckSquare className={`w-5 h-5 ${task.is_completed ? 'text-green-600' : 'text-gray-400'}`} />
                      <span className={task.is_completed ? 'line-through text-gray-500' : ''}>{task.title}</span>
                    </div>
                  ))
                ) : (
                  <p className="text-gray-400 text-center py-8">Aucune tâche</p>
                )}
              </div>
            </div>
          )}

          {activeTab === 'history' && (
            <div className="space-y-4">
              {card.history?.length > 0 ? (
                card.history.slice().reverse().map(entry => (
                  <div key={entry.id} className="flex gap-4 p-4 bg-gray-50 rounded-lg">
                    <div className="w-2 h-2 bg-blue-500 rounded-full mt-2" />
                    <div className="flex-1">
                      <p className="text-gray-900">{entry.description}</p>
                      <p className="text-xs text-gray-500 mt-1">
                        {entry.created_by} • {new Date(entry.created_at).toLocaleString('fr-FR')}
                      </p>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-gray-400 text-center py-8">Aucun historique</p>
              )}
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
}
